import { mpan_search_limit } from "../config/constants";
import { GasMeterDetails } from "../types/components.types";
import {
  GasMeterSearchResult,
  GasMeterDataResponse,
  MultiMprnResult,
  GasAddressSearchApiResponse,
  GasAddressSearchResponse,
  GasMeterDetailsByAddressApiResponse,
  GasSwitchDataItem,
  GasAddressSearchParams,
} from "../types/services.types";

import { apiGet, isHttpError } from "./apiService";

const endpoints = {
  mprn_search: "/electralink/mprn",
  gas_postcode_search: "/electralink/mprn/postcode",
  gas_addressid_search: "/electralink/mprn/addressid",
};

// // The data from the middleware is returned in nested objects.
// // We need to unnest these objects in order to be able to export the entire data to CSV.
// // TS type is any for now.
// // Prefix is a string that will hold the path of the keys through nested objects, so we know where each value came from.
// const unnestedData = (object: any, prefix = ""): Record<string, string> => {
//   // Create an empty object to store the final 'flattened' result. Each key is the path to the original nested value.
//   const result: Record<string, string> = {};

//   // Loop through every key in a given object (from the API response).
//   for (const key in object) {
//     if (object.hasOwnProperty(key)) {
//       // If a prefix exists, build a new key name by combining the prefix and the current key, separated by an underscore e.g.,
//       // prefix = 'payload_additional_detail_0' key = 'additional_detail_msn' will result in: prefixedKey = "payload_additional_detail_0_additional_detail_msn";
//       // The key will reflect the full path in the original nested object structure, so we know where each value came from.
//       const prefixedKey = prefix ? `${prefix}_${key}` : key;

//       // Get the value from the object using the current key.
//       const value = object[key];

//       // If a a value is an object/array and not null, you need to flatten it too. This is useful for objects/arrays like: additional_detail and tpr_data from the API response.
//       if (typeof value === "object" && value !== null) {
//         // Call the unnestedData again and pass it the nested object/array (value) and its path in 'prefixedKey' to flatten it too.
//         const nestedResult = unnestedData(value, prefixedKey);

//         // Merge each key-value pair from the nested result into the main result, so you can accumulate all the flattened values into one single object.
//         for (const nestedKey in nestedResult) {
//           if (nestedResult.hasOwnProperty(nestedKey)) {
//             result[nestedKey] = nestedResult[nestedKey];
//           }
//         }
//       } else {
//         // If the value is not an object (e.g., string/number), add it to the main result using the prefixed key to preserve its original path.
//         result[prefixedKey] = value;
//       }
//     }
//   }
//   return result;
// };

// Encapsulate the logic for building the meterDetails object in one place (optionally including the TAQ field), to avoid repeating the same object for other endpoints.
const createGasMeterDetails = (data: GasMeterDataResponse): GasMeterDetails => {
  const gasMeterDetails: GasMeterDetails = {
    houseName: data.houseName,
    subBuildingName: data.subBuildingName,
    houseNumber: data.houseNumber,
    dependentStreet: data.dependentStreet,
    street: data.street,
    postTown: data.postTown,
    postcode: data.postcode,
    county: data.county,
    currentSupplierId: data.currentSupplierId,
    current_supplier_name: data.current_supplier_name,
    currentSupplierRegEffectiveDate: data.currentSupplierRegEffectiveDate,
    pendingSupplierRegEffectiveDate: data.pendingSupplierRefEffectiveDate,
    mprn: data.mprn,
    meterSerialNumber: data.meterSerialNumber,
    mpaq: data.mpaq,
  };
  console.log("createMeterDetails object is built:", data);

  return gasMeterDetails;
};

const formatDateEntries = (gasMeterDetails: GasMeterDetails): GasMeterDetails => {
  const formattedDetails = { ...gasMeterDetails };

  if (formattedDetails.currentSupplierRegEffectiveDate) {
    let rawDateValue = formattedDetails.currentSupplierRegEffectiveDate;
    if (typeof formattedDetails.currentSupplierRegEffectiveDate !== "string") {
      rawDateValue = String(formattedDetails.currentSupplierRegEffectiveDate);
    }
    const dateString =
      rawDateValue.slice(0, 4) +
      "-" +
      rawDateValue.slice(4, 6) +
      "-" +
      rawDateValue.slice(6, 8) +
      "T00:00:00";
    const date = new Date(dateString);
    formattedDetails.currentSupplierRegEffectiveDate = date.toLocaleDateString("en-GB");
  }

  if (formattedDetails.pendingSupplierRegEffectiveDate) {
    const dateString =
      formattedDetails.pendingSupplierRegEffectiveDate.slice(0, 4) +
      "-" +
      formattedDetails.pendingSupplierRegEffectiveDate.slice(4, 6) +
      "-" +
      formattedDetails.pendingSupplierRegEffectiveDate.slice(6, 8) +
      "T00:00:00";
    const date = new Date(dateString);
    formattedDetails.pendingSupplierRegEffectiveDate = date.toLocaleDateString("en-GB");
  }

  return formattedDetails;
};

// Mpan Tab Search
export const searchMeterByMprn = async (
  mprn: GasMeterDetails["mprn"],
): Promise<GasMeterSearchResult> => {
  // Mprn is between 6 and 11 digits.
  const mprnRegex = /^[0-9]{6,11}$/;

  // Remove any whitespaces from the MPRN input.
  const cleanMprn = mprn.replace(/\s/g, "");

  // Test the cleanMprn against the regex.
  const isValid = mprnRegex.test(cleanMprn);

  // Return early if the Mprn is invalid.
  if (!isValid) {
    console.log("Invalid MPRN provided");
    return { isValid, gasMeterDetails: null };
  }

  try {
    // If the Mpan is valid, make a GET request to the API endpoint, passing the clean MPAN.
    const data = await apiGet<GasMeterDataResponse>(`${endpoints.mprn_search}/${cleanMprn}`);
    console.log("Mprn API response data:", data);
    console.log("Mprn API response status code:", data.status_code);
    // Check if the API request is successful and contains the required data.
    if (data.status_code === 200 || "mpaq" in data) {
      console.log("Mprn data:", data);

      // Create meter details with conditional TAQ data.
      let gasMeterDetails = createGasMeterDetails(data);
      gasMeterDetails = formatDateEntries(gasMeterDetails);
      return { isValid: true, gasMeterDetails };

      // Throw an error if no data was returned.
    } else {
      throw new Error("No data returned from the API.");
    }
  } catch (error) {
    console.log("Error fetching meter details:", error);
    throw error;
  }
};

// Search for multiple MPRNs in parallel. The backend doesn't provide a bulk search endpoint, so we need to make individual requests.
export const searchMultipleMetersByMprn = async (mprns: string[]): Promise<MultiMprnResult[]> => {
  // Implement a maximum limit to prevent excessive API calls.
  if (mprns.length > mpan_search_limit) {
    throw new Error(`Maximum ${String(mpan_search_limit)} MPRNs allowed per search`);
  }

  // Map each MPRN to a promise. Since there's no bulk endpoint, we need to fire off individual requests in parallel to reduce the total wait time.
  const promises = mprns.map(async (mprn) => {
    try {
      // Reuse the existing single-MPRN search function to avoid duplicating validation and API logic.
      const result = await searchMeterByMprn(mprn);
      return {
        mprn,
        success: true,
        gasMeterDetails: result.gasMeterDetails,
      };
    } catch (error) {
      // Catch any thrown errors (e.g. network failures) and return a failed result. This ensures one failed MPRN doesn't break the entire batch -- other results are still returned.
      return {
        mprn,
        success: false,
        gasMeterDetails: null,
        error: "Failed to retrieve meter details",
      };
    }
  });

  // Wait for all promises to resolve.
  // Every MPRN will return a result (success or failure).
  return Promise.all(promises);
};

// Search for gas meters by postcode with an optional house name or house number filter.
export const searchGasAddressesByPostcode = async ({
  postcode,
  houseName,
  houseNumber,
}: GasAddressSearchParams): Promise<GasAddressSearchResponse> => {
  // Postcode validation.
  const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i;

  // Remove any whitespace and validate postcode format.
  const cleanPostcode = postcode.replace(/\s/g, "");
  const isValid = postcodeRegex.test(cleanPostcode);

  if (!isValid) {
    return { success: false, message: "Invalid postcode format." };
  }

  try {
    // Build the URL with optional query params for house name or house number
    let postcodeUrl = `${endpoints.gas_postcode_search}/${cleanPostcode}/`;

    // Add query parameter if the house name is provided.
    // encodeURIComponent is a built-in JS func. that encodes special characters in a string for safe URL usage.
    if (houseName?.trim()) {
      postcodeUrl += `?housename=${encodeURIComponent(houseName.trim())}`;
    }
    // Add a query parameter if the house number is provided.
    else if (houseNumber?.trim()) {
      postcodeUrl += `?housenumber=${encodeURIComponent(houseNumber.trim())}`;
    }

    const data = await apiGet<GasAddressSearchApiResponse>(postcodeUrl);

    if (data.status_code === 200) {
      return {
        success: true,
        addresses: data.json.switchAddresses,
      };
    } else {
      console.log("Invalid data format received from the middleware.");
      throw new Error("Invalid data format received from the middleware.");
    }
  } catch (error) {
    console.log("Error searching for gas addresses by postcode:", error);

    if (isHttpError(error)) {
      const statusCode = error.response.status;
      if (statusCode === 404) {
        return { success: false, message: "No addresses found for this postcode." };
      } else if (statusCode === 500 || statusCode === 502) {
        return { success: false, message: "Server is down. Please try again later." };
      } else {
        return { success: false, message: `Error (${String(statusCode)}). Please try again.` };
      }
    }
    throw error;
  }
};

// Gas meter details by address ID.
export const getGasMeterDetailsByAddressId = async (
  addressId: string,
): Promise<GasSwitchDataItem> => {
  try {
    const data = await apiGet<GasMeterDetailsByAddressApiResponse>(
      `${endpoints.gas_addressid_search}/${addressId}`,
    );

    if (data.status_code === 200 && data.json.switchGasData.length > 0) {
      console.log("Gas meter details retrieved:", data.json.switchGasData[0]);
      return data.json.switchGasData[0];
    } else {
      console.log("No meter details found for this address.");
      throw new Error("No meter details found for this address.");
    }
  } catch (error) {
    console.log("Error fetching gas meter details by address ID:", error);
    throw error;
  }
};
